import React from 'react';
import ReactApexChart from 'react-apexcharts';

const SmallChart = ({ seed = 1, targetPercentage = 12.53 }) => {
  // Generate random increasing data points that reach the target percentage
  const generateData = (seed, targetPercentage) => {
    let data = [];
    const points = 20;
    // Start from a random percentage between 30-70% of target
    let value = targetPercentage * (0.3 + Math.random() * 0.4);
    const volatility = 0.8 + (seed * 0.4); // Increased volatility
    
    // Calculate average increment needed to reach target
    const totalDistance = targetPercentage - value;
    const avgIncrement = totalDistance / points;

    for (let i = 0; i < points; i++) {
      // Add more realistic random movements
      const randomDirection = Math.random() > 0.4 ? 1 : -1; // 60% chance of going up
      const randomMove = (Math.random() * volatility * avgIncrement);
      
      if (i === points - 1) {
        // Last point should be exactly the target
        value = targetPercentage;
      } else {
        // Add random movement but ensure we're generally trending towards target
        value += (avgIncrement * 0.5) + (randomDirection * randomMove);
        // Prevent going too low or too high
        value = Math.max(targetPercentage * 0.2, Math.min(targetPercentage * 1.5, value));
      }
      
      data.push(parseFloat(value.toFixed(2)));
    }
    return data;
  };

  const series = [{
    name: 'ROI',
    data: generateData(seed, targetPercentage)
  }];

  // Determine colors based on performance
  const isPositive = targetPercentage >= 0;
  const chartColor = isPositive ? '#00E396' : '#FF4757';
  const gradientStart = isPositive ? '#00E396' : '#FF4757';
  const gradientEnd = isPositive ? '#00D084' : '#FF3742';

  const options = {
    chart: {
      type: 'area',
      toolbar: {
        show: false
      },
      sparkline: {
        enabled: true
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 1200,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      background: 'transparent'
    },
    stroke: {
      curve: 'smooth',
      width: 2.5,
      colors: [chartColor]
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        type: 'vertical',
        colorStops: [
          {
            offset: 0,
            color: gradientStart,
            opacity: 0.8
          },
          {
            offset: 50,
            color: gradientStart,
            opacity: 0.4
          },
          {
            offset: 100,
            color: gradientEnd,
            opacity: 0.1
          }
        ]
      }
    },
    colors: [chartColor],
    tooltip: {
      enabled: true,
      theme: 'dark',
      style: {
        fontSize: '12px',
        backgroundColor: '#1E2329'
      },
      marker: {
        show: true
      },
      y: {
        formatter: (value) => `${value.toFixed(2)}%`
      },
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const value = series[seriesIndex][dataPointIndex];
        return `
          <div style="
            background: linear-gradient(135deg, #1E2329 0%, #252930 100%);
            border: 1px solid rgba(2, 192, 118, 0.2);
            border-radius: 8px;
            padding: 8px 12px;
            color: #FFFFFF;
            font-size: 12px;
            font-weight: 600;
          ">
            <div style="color: ${chartColor};">
              ${value.toFixed(2)}% ROI
            </div>
          </div>
        `;
      }
    },
    grid: {
      show: false,
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    xaxis: {
      labels: {
        show: false
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      crosshairs: {
        show: false
      }
    },
    yaxis: {
      show: false,
      min: Math.min(...series[0].data) * 0.95,
      max: Math.max(...series[0].data) * 1.05
    },
    dataLabels: {
      enabled: false
    },
    markers: {
      size: 0,
      strokeWidth: 0,
      hover: {
        size: 4,
        sizeOffset: 2
      }
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1
        }
      }
    }
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '55px',
      position: 'relative',
      background: 'rgba(0, 0, 0, 0.08)',
      borderRadius: '6px',
      overflow: 'hidden'
    }}>
      <ReactApexChart
        options={options}
        series={series}
        type="area"
        height="100%"
        width="100%"
      />
      
      {/* Performance indicator overlay */}
      <div style={{
        position: 'absolute',
        top: '6px',
        right: '6px',
        background: `rgba(${isPositive ? '0, 227, 150' : '255, 71, 87'}, 0.08)`,
        color: chartColor,
        padding: '1px 4px',
        borderRadius: '3px',
        fontSize: '8px',
        fontWeight: '600',
        border: `1px solid rgba(${isPositive ? '0, 227, 150' : '255, 71, 87'}, 0.15)`
      }}>
        {isPositive ? '↗' : '↘'} {Math.abs(targetPercentage).toFixed(1)}%
      </div>
    </div>
  );
};

export default SmallChart;